<i18n src="./resources/locales.json"></i18n>

<template>
  <div id="interaction_header_container" class="wrapper px-2">
    <div class="bp-info">
      {{ businessProcessInfo }}
    </div>
    <div class="contact-info">
      <span class="contact-basic-info">
        <span v-show="interaction.isConsultReceiver && !interaction.isConference" class="additional-info">
          {{ $t('labels.onBehalfOf') }}
        </span>
        <span class="name">{{ basicInfo }}</span>
      </span>
      <span class="contact-additional-info">
        {{ additionalInfo }}
      </span>
    </div>
    <div class="additional-info">
      <span v-if="showQueueWaitTime(interaction)" class="key-value-props">
        <span v-once class="label">{{ $t('labels.queueWaitTime') }}</span>
        <span class="value">{{ formatWaitTime(interaction.timing.waitingTime) }}</span>
      </span>
      <span v-if="hasRoutingInfo" class="key-value-props">
        <span v-once class="label">{{ $t('labels.routingInfo') }}</span>
        <span class="value">{{ formatWaitTime(interaction.routing.routingInfo) }}</span>
      </span>
    </div>
    <div v-if="!interaction.isConsultReceiver" class="demand-chips">
      <v-chip v-for="demand in interaction.parsedDemands" :key="demand" class="mr-2" color="accent" small>
        {{ demand }}
      </v-chip>
    </div>
    <div class="avatar">
      <v-hover :disabled="!isAvatarEditable">
        <v-avatar slot-scope="{ hover }" size="avatarSize">
          <EcsInteractionAvatar ref="interactionAvatar" :interaction="interaction" :size="avatarSize" />
          <v-expand-transition>
            <div v-if="hover && isAvatarEditable" class="d-flex transition-fast-in-fast-out v-card--reveal">
              <v-btn
                class="customer-profile-button white--text"
                color="white"
                text
                @click="$refs.updateContactAvatarDialog.open()"
              >
                <div v-once>
                  {{ $t('buttons.updateContactPhoto') }}<br />
                  <v-icon>mdi-camera</v-icon>
                </div>
              </v-btn>
            </div>
          </v-expand-transition>
        </v-avatar>
      </v-hover>
    </div>

    <!-- Photo Update Dialog -->
    <EcsUpdateProfilePhotoDialog ref="updateContactAvatarDialog" @save-profile-photo="handleUpdateContactAvatar" />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import EcsUpdateProfilePhotoDialog from '@/components/ecs-agent-info/EcsUpdateProfilePhotoDialog';
import EcsInteractionMixin from '@/mixins/ecs-interaction-mixin/EcsInteractionMixin';
import EcsInteractionAvatar from '@/components/ecs-base-interaction-components/ecs-avatar/EcsInteractionAvatar';
import EcsInteractionQueueWaitTimeMixin from '@/mixins/ecs-interaction-mixin/EcsInteractionQueueWaitTimeMixin';

export default {
  name: 'InteractionHeader',

  components: { EcsInteractionAvatar, EcsUpdateProfilePhotoDialog },

  mixins: [EcsInteractionMixin, EcsInteractionQueueWaitTimeMixin],

  props: {
    interaction: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      avatarSize: 60
    };
  },

  computed: {
    ...mapGetters('AGENT', ['loggedInAgent']),

    ...mapGetters('AVATARS', ['getContactAvatar', 'getAgentAvatar']),

    businessProcessInfo() {
      let result = '';
      const { routing, isCustomerHandling, isConsultReceiver } = this.interaction;

      if (isCustomerHandling && !isConsultReceiver) {
        result = `${routing.businessProcessName} - ${routing.destination}`;
      } else if (this.interaction.isDirectCall) {
        result = this.$t('labels.directCall');
      } else {
        result = routing.businessProcessName;
      }

      return result;
    },

    isAvatarEditable() {
      let result =
        !this.interaction.isConference &&
        !this.interaction.isConsultReceiver &&
        !this.interaction.isOnWrapUp &&
        !this.interaction.isInternal;

      if (this.interaction.isCustomerHandling) {
        result =
          result && this.loggedInAgent.crm === 'Enabled' && this.interaction.customer && this.interaction.customer.id;
      }

      return result;
    },

    hasRoutingInfo() {
      return this.interaction.routing && this.interaction.routing.routingInfo;
    },

    basicInfo() {
      return this.interaction.isOutgoingEmail && !this.interaction.receptor.name
        ? this.$t('newEmail')
        : this.interaction.formattedCustomerFullName || this.interaction.receptor.name;
    },

    additionalInfo() {
      return (this.interaction.isChat && this.interaction.isCustomerHandling) || this.interaction.isEmail
        ? this.interaction.receptor.email
        : this.interaction.receptor.endPoint;
    }
  },

  methods: {
    ...mapActions('AVATARS', ['upsertAvatar']),

    handleUpdateContactAvatar(avatar) {
      if (avatar) {
        const receptor = this.interaction.receptor;
        this.upsertAvatar({ [receptor.isAgent ? 'agentId' : 'contactId']: receptor.id, avatar });
      }
    }
  }
};
</script>

<style scoped>
.bp-info {
  grid-row: 1;
  width: 100%;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  align-self: center;
  justify-self: start;
  opacity: 0.75;
}
.contact-info {
  grid-row: 2;
  width: 100%;
  font-size: 14px;
  align-self: center;
  justify-self: start;
}
.contact-basic-info {
  font-size: 18pt;
}
.contact-basic-info .name {
  color: var(--v-primary-base);
}
.contact-additional-info {
  opacity: 0.75;
}
.demand-chips {
  grid-row: 4;
  grid-column: 1/3;
  align-self: center;
  justify-self: start;
}
.demand-chips .v-chip {
  height: 22px;
}
.additional-info {
  grid-row: 3;
  margin-top: -6px;
  font-size: 12px;
}
.key-value-props + .key-value-props {
  padding-left: 8px;
}
.additional-info .label {
  opacity: 0.75;
  padding-right: 4px;
}
.additional-info .value {
  font-weight: bold;
}
.avatar {
  align-self: center;
  justify-self: end;
  grid-row: 1/4;
}
.wrapUpTimer-enter-active {
  animation: scale-in 250ms;
}
.wrapUpTimer-leave-active {
  animation: scale-in 250ms reverse;
}
/* CSS Animation Keyframes */
@keyframes scale-in {
  0% {
    transform: scale(0);
    opacity: 1;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}
#interaction_header_container {
  display: grid;
  grid-template-columns: auto 72px;
  grid-template-rows: 22px 36px 22px 24px;
}
.wrapper {
  align-items: start;
  height: 104px;
  overflow: hidden;
  width: 100%;
}
.wrapper > div {
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
}

.v-card--reveal {
  position: absolute;
  height: 100%;
  width: 100%;
  bottom: 0;
  align-items: center;
  justify-content: center;
  background-color: transparent !important;
}

.customer-profile-button {
  width: 100%;
  height: 100% !important;
  border-radius: 50%;
  background-color: rgba(0, 0, 0, 0.4);
  font-size: 8px;
}
</style>
